import { backendApi } from "./backendApi.rtkq";

/**
 * Defines a component (an app) within a Plan.
 * Each component has its own price and contributes to the total plan.
 */
export interface AppPlan {
  appIdentifier: string;
  price: number;
  includedCredits?: number;
  creditCost?: number;
  /** Soft unlimited — backend stores includedCredits = 1_000_000 when true. */
  unlimited?: boolean;
}

/**
 * Represents a complete billing plan.
 * NOTE: The total price and total credits of the plan are CALCULATED dynamically
 * by summing the 'price' and 'includedCredits' from all items in the 'includedApps' array.
 * They are not stored directly on the Plan document itself.
 */
export interface Plan {
  planId?: string;
  name: string;
  internalName: string;
  priceMonthly: number;
  monthlyCreditAllocation: number;
  periodDays?: number;
  quotaWindowDays?: number;
  monthlyBudgetUsd?: number;
  isActive?: boolean;
  /** When true, new offices on signup use this plan. At most one plan. */
  isSignupDefault?: boolean;
  includedApps: AppPlan[];
  /** Feature flags (e.g. 'agent-cloud', 'mcp-connector'). */
  features?: string[];
  description?: string;
  officeCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdatePlanParams {
  planId: string;
  updates: Partial<Omit<Plan, "planId" | "createdAt">>;
}

export interface DeletePlanParams {
  planId: string;
}

export interface GetPlanByIdParams {
  planId: string;
}

export interface GetPlansByPriceRangeParams {
  minPrice: number;
  maxPrice: number;
}

export interface EntitlementVocabulary {
  version: number;
  planFeatures: string[];
  connectors?: string[];
  channels?: string[];
}

export const plansApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    getEntitlementVocabulary: builder.query<EntitlementVocabulary, void>({
      query: () => "/api/entitlements/vocabulary",
    }),

    // Get all plans
    getPlans: builder.query<Plan[], void>({
      query: () => "/plans",
      transformResponse: (response: any) => {
        const plans = response.plans || response || [];
        return plans.map((plan: any) => ({
          ...plan,
          planId: plan.planId || plan.id,
        }));
      },
      providesTags: ["Plans"],
    }),

    // Get plan by ID
    getPlanById: builder.query<Plan | null, GetPlanByIdParams>({
      query: ({ planId }) => `/plans/${planId}`,
      transformResponse: (response: any) => {
        if (!response) return null;
        return {
          ...response,
          planId: response.planId || response.id,
        };
      },
      providesTags: (result, _error, { planId }) => (result ? [{ type: "Plans", id: planId }] : []),
    }),

    // Create new plan
    createPlan: builder.mutation<{ planId: string; plan: Plan }, Plan>({
      query: (planData) => ({
        url: "/plans",
        method: "POST",
        body: planData,
      }),
      transformResponse: (response: any) => ({
        planId: response.planId || response.id,
        plan: {
          ...response,
          planId: response.planId || response.id,
        },
      }),
      invalidatesTags: ["Plans"],
    }),

    // Update existing plan
    updatePlan: builder.mutation<{ success: boolean }, UpdatePlanParams>({
      query: ({ planId, updates }) => ({
        url: `/plans/${planId}`,
        method: "PUT",
        body: updates,
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (result, _error, { planId }) => (result ? [{ type: "Plans", id: planId }, "Plans"] : []),
    }),

    getArchivedPlans: builder.query<Plan[], void>({
      query: () => "/plans/archived",
      transformResponse: (response: any) => {
        const plans = response.plans || response || [];
        return plans.map((plan: any) => ({
          ...plan,
          planId: plan.planId || plan.id,
        }));
      },
      providesTags: ["Plans"],
    }),

    restorePlan: builder.mutation<Plan, { planId: string }>({
      query: ({ planId }) => ({
        url: `/plans/${planId}/restore`,
        method: "POST",
      }),
      transformResponse: (response: any) => ({
        ...response,
        planId: response.planId || response.id,
      }),
      invalidatesTags: ["Plans", "Accounts"],
    }),

    // Archive plan (soft: is_active = false)
    deletePlan: builder.mutation<{ success: boolean }, DeletePlanParams>({
      query: ({ planId }) => ({
        url: `/plans/${planId}`,
        method: "DELETE",
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: ["Plans", "Accounts"],
    }),

    // Get plans by price range — fetches all plans and filters client-side
    getPlansByPriceRange: builder.query<Plan[], GetPlansByPriceRangeParams>({
      query: () => "/plans",
      transformResponse: (response: any, _meta, { minPrice, maxPrice }: GetPlansByPriceRangeParams) => {
        const plans = response.plans || response || [];
        return plans
          .filter((plan: any) => plan.priceMonthly >= minPrice && plan.priceMonthly <= maxPrice)
          .map((plan: any) => ({
            ...plan,
            planId: plan.planId || plan.id,
          }))
          .sort((a: Plan, b: Plan) => a.priceMonthly - b.priceMonthly);
      },
      providesTags: (result, _error, { minPrice, maxPrice }) =>
        result ? [{ type: "Plans", id: `price-range-${minPrice}-${maxPrice}` }] : [],
    }),
  }),
});

export const {
  useGetEntitlementVocabularyQuery,
  useGetPlansQuery,
  useGetPlanByIdQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
  useGetArchivedPlansQuery,
  useRestorePlanMutation,
  useGetPlansByPriceRangeQuery,
} = plansApi;
